import { useEffect, useMemo, useState } from 'react';
import {
  DashboardView,
  DashboardViewUpdatePayload
} from '@experiment-management-shared/types';
import useExperiments from '@API/experiments/useExperiments';
import { hasDashboardAnyRule } from '@experiment-management-shared/utils/EMView';
import { getRulesCount } from '@shared/utils/filterHelpers';
import { get, isEqual, uniqBy } from 'lodash';
import { ExperimentDetails } from '@shared/types';
import difference from 'lodash/difference';
import useDeepMemo from '@shared/hooks/useDeepMemo';
import { useDashboardApiContext } from '@projects/components/EMDashboardPage/useDashboardAPIContext';

export type DashboardExperiment = ExperimentDetails & {
  isInPinnedGroup?: boolean;
};
type UseDashboardExperimentsDataOpts = {
  isArchiveSection: boolean;
  experimentKeysForExperimentsTab: string[] | null;
  refetchInterval: boolean | number;
  dashboardView: DashboardView | null;
  groupsQuery: string;
  viewId?: string;
  updateDashboardView: (data: DashboardViewUpdatePayload) => void;
};
const useDashboardExperimentsData = ({
  isArchiveSection,
  experimentKeysForExperimentsTab,
  refetchInterval,
  groupsQuery,
  dashboardView,
  viewId,
  updateDashboardView
}: UseDashboardExperimentsDataOpts) => {
  const { extraColumns, isLoadingExtraColumns } = useDashboardApiContext();
  const [selectedExperiments, setSelectedExperiments] = useState<
    DashboardExperiment[]
  >([]);

  const {
    data: experimentsData,
    isFetching: isFetchingExperiments,
    isPreviousData: isPreviousDataExperiments,
    isSuccess: areExperimentsLoaded
  } = useExperiments(
    {
      isArchive: isArchiveSection,
      groupsQuery,
      targetExperimentKeys: experimentKeysForExperimentsTab,
      view: dashboardView,
      viewId,
      columns: extraColumns,
      isLoadingColumns: isLoadingExtraColumns
    },
    { refetchInterval, refetchOnMount: true }
  );

  const {
    data: pinnedExperimentData,
    isFetching: isFetchingPinnedExperiments,
    isPreviousData: isPreviousDataPinnedExperiments,
    isSuccess: arePinnedExperimentsLoaded
  } = useExperiments(
    {
      ignorePagination: true,
      targetExperimentKeys: dashboardView?.pinnedExperimentKeys || [],
      view: dashboardView,
      viewId,
      columns: extraColumns,
      isLoadingColumns: isLoadingExtraColumns
    },
    {
      initialData: {
        experiments: [],
        total: 0
      },
      refetchInterval,
      refetchOnMount: true,
      enabled: !!dashboardView?.pinnedExperimentKeys?.length
    }
  );

  const rulesCount = getRulesCount(dashboardView?.query?.rulesTree);

  const tableExperiments = useMemo(() => {
    const experiments: ExperimentDetails[] = experimentsData?.experiments || [];

    if (isArchiveSection) {
      return experiments;
    }

    const isPinnedExperimentsEmpty =
      dashboardView &&
      hasDashboardAnyRule(dashboardView) &&
      !experiments.length;

    let pinnedExperiments: ExperimentDetails[] = [];
    const hasPinnedExperiments =
      !isPinnedExperimentsEmpty && pinnedExperimentData?.experiments?.length;

    if (hasPinnedExperiments) {
      pinnedExperiments = pinnedExperimentData.experiments;
    }

    const pinnedExperimentsGroup = pinnedExperiments.map(experiment => ({
      ...experiment,
      isInPinnedGroup: true
    }));

    const pinnedExperimentKeys = pinnedExperimentsGroup.map(
      ({ experimentKey }) => experimentKey
    );

    const unpinnedExperimentsGroup = experiments
      .filter(({ experimentKey }) => {
        return !pinnedExperimentKeys.includes(experimentKey);
      })
      .map((experiment, index) => {
        if (index === 0 && pinnedExperimentsGroup.length) {
          return {
            ...experiment,
            isFirstOfUnpinnedGroup: true
          };
        }

        return experiment;
      });

    return [...pinnedExperimentsGroup, ...unpinnedExperimentsGroup];
  }, [
    experimentsData?.experiments,
    isArchiveSection,
    pinnedExperimentData?.experiments,
    rulesCount
  ]);

  const totalExperiments = get(experimentsData, 'total', 0);

  const panelExperiments = useMemo(
    () => uniqBy(tableExperiments, 'experimentKey'),
    [tableExperiments]
  );

  const panelExperimentKeys = useMemo(() => {
    return panelExperiments.map(({ experimentKey }) => experimentKey) || [];
  }, [panelExperiments]);

  const visibleExperimentKeys = useDeepMemo(() => {
    return difference(
      panelExperimentKeys,
      dashboardView?.hiddenExperimentKeys || []
    );
  }, [panelExperimentKeys, dashboardView?.hiddenExperimentKeys]);

  const isFetchingAnyExperiments =
    isFetchingExperiments || isFetchingPinnedExperiments;
  const isPreviousDataAnyExperiments =
    isPreviousDataExperiments || isPreviousDataPinnedExperiments;
  const isLoadingAnyExperiments =
    !areExperimentsLoaded || !arePinnedExperimentsLoaded;

  useEffect(() => {
    setSelectedExperiments(previousSelectedExperiments => {
      const combinedExperiments = [
        ...tableExperiments,
        ...previousSelectedExperiments
      ];

      const selectedKeys = dashboardView?.table.selection || [];
      return selectedKeys
        .map(experimentKey =>
          combinedExperiments.find(e => experimentKey.includes(e.experimentKey))
        )
        .filter((exp): exp is ExperimentDetails => exp !== undefined);
    });
  }, [dashboardView?.table.selection, tableExperiments]);

  // Filter any archived pinned experiments. When the view is
  // eventually saved, those pinned experiments will be removed.
  useEffect(() => {
    if (
      !dashboardView?.pinnedExperimentKeys?.length ||
      isFetchingPinnedExperiments ||
      !arePinnedExperimentsLoaded
    ) {
      return;
    }

    const pinnedExperiments: ExperimentDetails[] =
      pinnedExperimentData?.experiments || [];

    const filtered = pinnedExperiments
      .filter(exp => !exp.archived)
      .map(exp => exp.experimentKey);

    if (!isEqual(filtered, dashboardView?.pinnedExperimentKeys)) {
      updateDashboardView({
        pinnedExperimentKeys: filtered
      });
    }
  }, [
    arePinnedExperimentsLoaded,
    dashboardView?.pinnedExperimentKeys,
    updateDashboardView,
    isFetchingPinnedExperiments,
    pinnedExperimentData
  ]);

  return {
    tableExperiments,
    selectedExperiments,
    totalExperiments,
    panelExperiments,
    isFetchingAnyExperiments,
    isPreviousDataAnyExperiments,
    isLoadingAnyExperiments,
    visibleExperimentKeys,
    isExperimentsDataReady: arePinnedExperimentsLoaded && areExperimentsLoaded
  };
};

export default useDashboardExperimentsData;
